import { Trans } from '@lingui/react'
import classNames from 'clsx'
import { arrayOf, bool, func, number, shape, string } from 'prop-types'
import { Component } from 'react'
import { connectRange } from 'react-instantsearch-dom'

import { FILTERS_DEBOUNCE_TIME } from 'shared/experiments/components/NewSerp/consts'
import FlyoutFilter from 'shared/experiments/components/NewSerp/FlyoutFilter/FlyoutFilter'
import { withSearchStateHandler } from 'shared/experiments/components/NewSerp/helpers'
import ListItem from 'shared/experiments/components/NewSerp/StarRatingStacked/ListItem'
import { SERP_FILTER_APPLY } from 'shared/experiments/consts'
import trackEvent from 'shared/experiments/utils/trackOptimizelyEvent'
import { subscribe } from 'shared/store'
import { PRODUCT_FILTER_RESET } from 'shared/store/ducks/events'
import { type InferPropsWithDefaults } from 'shared/utils/propTypes'
import IconBin from 'views/components/atoms/Icon/IconBin'
import ButtonAsLink from 'views/components/molecules/ButtonAsLink'
import StarRatingSlider from 'views/components/molecules/StarRatingSlider'
import TextWithIcon from 'views/components/molecules/TextWithIcon'
import {
  getRealMaxForRatings,
  getRealMinForRatings,
} from 'views/components/organisms/AlgoliaInstantSearch/Algolia/helpers'
import { AVERAGE_RATING_DEFAULT_VALUES } from 'views/components/organisms/AlgoliaInstantSearch/ProductFilter/consts'
import debounce from 'views/utils/debounce'

import { MAX_RATING } from './consts'

import './StarRatingStacked.scss'

class StarRatingStacked extends Component<ComponentProps, ComponentState> {
  subscriptions: CallableFunction[] = []
  state = this.getInitialState()

  constructor(props: ComponentProps) {
    super(props)

    this.subscriptions.push(
      subscribe.after(PRODUCT_FILTER_RESET, this.handleResetClick)
    )
  }

  getInitialState(): ComponentState {
    const { defaultValues = {} } = this.props
    const minValue =
      defaultValues?.minValue ?? AVERAGE_RATING_DEFAULT_VALUES.min
    const maxValue =
      defaultValues?.maxValue ?? AVERAGE_RATING_DEFAULT_VALUES.max

    return {
      values: [minValue, maxValue],
    }
  }

  componentWillUnmount() {
    this.subscriptions.forEach(unsub => unsub())
  }

  /**
   * No direct call: component uses debounced call via `this.refine`
   */
  #refineWorker = () => {
    const { values = [] } = this.state
    const [min, max] = values

    withSearchStateHandler(() => {
      this.props.refine({
        min: getRealMinForRatings(min),
        max: getRealMaxForRatings(max),
      })
    })
  }
  refine: () => void = debounce(this.#refineWorker, FILTERS_DEBOUNCE_TIME)

  hasAdjustedValues(): boolean {
    const {
      currentRefinement: { min },
    } = this.props
    const { min: minRange } = AVERAGE_RATING_DEFAULT_VALUES

    return min > minRange
  }

  handleClick = (starsNumber: string) => {
    this.setState(
      {
        values: [
          Math.min(parseInt(starsNumber, 10) || 0, MAX_RATING),
          MAX_RATING,
        ],
      },
      this.refine
    )
    trackEvent(SERP_FILTER_APPLY)
  }

  handleResetClick = () => {
    const { min, max } = AVERAGE_RATING_DEFAULT_VALUES

    this.setState(
      {
        values: [min, max],
      },
      () => withSearchStateHandler(() => this.props.refine({ min, max }))
    )
  }

  render() {
    const { label, attribute, currentRefinement, hidden } = this.props
    const selected = Math.ceil(currentRefinement.min)
    const isAdjusted = this.hasAdjustedValues()

    return (
      <FlyoutFilter
        attribute={attribute}
        label={label}
        active={isAdjusted}
        count={isAdjusted ? 1 : 0}
        hidden={hidden}
      >
        <ul className="e-StarRatingStacked__list a-list-reset">
          {Array(MAX_RATING)
            .fill(true)
            .map((_, index) => (
              <ListItem
                key={index}
                stars={MAX_RATING - index}
                onClick={this.handleClick}
                selected={selected}
              />
            ))}
        </ul>
        <div className="u-margin-large--top u-padding--top u-border--top e-RefinementList__StarRatingStacked__resetButton--wrapper">
          <ButtonAsLink
            className={classNames('o-RefinementList__resetButton', {
              'u-font-weight--bold': isAdjusted,
            })}
            onClick={this.handleResetClick}
            disabled={!isAdjusted}
          >
            <TextWithIcon icon={<IconBin size="small" />}>
              <Trans id="searchResults.label.clearFilter" />
            </TextWithIcon>
          </ButtonAsLink>
        </div>
      </FlyoutFilter>
    )
  }
}

const propTypes = {
  name: string.isRequired,
  delimiterLabel: string,
  values: arrayOf(number),
  snap: bool,
  hidden: bool,
  onValuesUpdated: func,
  onSliderDragEnd: func,
  onStarClick: func,
  label: string.isRequired,
  attribute: string.isRequired,
  currentRefinement: shape({ min: number.isRequired, max: number.isRequired })
    .isRequired,
  refine: func.isRequired,
  defaultValues: shape({
    minValue: number,
    maxValue: number,
  }),
}

const defaultProps = {
  ...StarRatingSlider.defaultProps,
  hidden: false,
  defaultValues: {
    minValue: AVERAGE_RATING_DEFAULT_VALUES.min,
    maxValue: AVERAGE_RATING_DEFAULT_VALUES.max,
  },
}

type ComponentProps = InferPropsWithDefaults<
  typeof propTypes,
  typeof defaultProps
>

type ComponentState = Partial<{
  values: number[]
}>

export default connectRange(StarRatingStacked)
